import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SiteMapUrl } from '../_enums/sitemap.url';
import { Role } from '../_models/role';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private userSubject!: BehaviorSubject<any>;
  public roleSubject!: BehaviorSubject<Role>;
  public regionSubject!: BehaviorSubject<string | undefined>;
  private selectedRole!: Role;
  private listAccesses: string[] = [];

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    this.userSubject = new BehaviorSubject<any>(JSON.parse(localStorage.getItem('user') || '{}'));

    this.roleSubject = new BehaviorSubject<Role>(JSON.parse(localStorage.getItem('userRole') || '{}'));
    this.roleSubject.subscribe((role) => (this.selectedRole = role));

    const storedRegion = localStorage.getItem('regionId') || undefined;
    this.regionSubject = new BehaviorSubject<string | undefined>(storedRegion);
  }

  public set roleValue(role: Role) {
    this.selectedRole = role;
  }

  public get roleValue(): Role {
    return this.selectedRole;
  }

  // Recuperer la liste des autorisations
  public get accessesValues(): string[] {
    return this.listAccesses;
  }

  public set accessesValues(list: string[]) {
    this.listAccesses = list;
  }

  public get userValue(): any {
    return this.userSubject?.value;
  }

  public get regionValue(): string | undefined {
    return this.regionSubject.value;
  }

  public set regionValue(value: string | undefined) {
    this.regionSubject.next(value);
  }

  public get isFranceRegion(): boolean {
    const region = this.regionSubject.value;
    return !region || region === '1';
  }

  public switchRegion(region: string) {
    localStorage.setItem('regionId', region);
    this.regionSubject.next(region);
    window.location.reload();
  }

  public get headers(): any {
    const h: Record<string, string> = {
      'X-Api-Key': `user_${this.userValue.key}`,
      'Role-Code': `${this.selectedRole?.profileCode}`,
      'Perimeter-Id': `${this.selectedRole?.perimeterId}`,
      'Access-Control-Allow-Origin': '*'
    };
    const region = this.regionSubject?.value;
    if (region) {
      h['Region-Id'] = region;
    }
    return new HttpHeaders(h);
  }

  login(login: string, password: string, rememberMe: boolean = false) {
    return this.http.post<any>(`${environment.apiUrl}/Auth`, { login, password, rememberMe }).pipe(
      map((data) => {
        if (data.success) {
          localStorage.setItem('user', JSON.stringify(data.user));
          this.userSubject.next(data.user);
        }
        return data;
      })
    );
  }

  ssoLogin(token: string) {
    // La clé de la session en cours part avec la demande : le serveur la révoque quand le lien FNTP
    // ouvre une session sur un autre compte, pour qu'un onglet resté ouvert cesse d'agir sous l'ancien.
    const previousKey = this.userValue?.key;
    const options = previousKey ? { headers: new HttpHeaders({ 'X-Api-Key': `user_${previousKey}` }) } : {};

    return this.http.post<any>(`${environment.apiUrl}/Auth/Sso`, { token }, options).pipe(
      map((data) => {
        if (data.success) {
          localStorage.setItem('user', JSON.stringify(data.user));
          this.userSubject.next(data.user);
        }
        return data;
      })
    );
  }

  async logout() {
    if (this.userValue?.key) {
      await this.http.get<any>(`${environment.apiUrl}/Auth/LogOut`, { headers: this.headers }).subscribe();
    }

    localStorage.removeItem('user');
    this.router.navigate([SiteMapUrl.MenuLogin]);
  }

  getAccesses(profileCode: string) {
    return this.http.get<string[]>(`${environment.apiUrl}/Auth/Accesses?profileCode=${profileCode}`, { headers: this.headers });
  }
}
